import { NgOptimizedImage } from '@angular/common';
import {
    Component,
    DOCUMENT,
    inject,
    Input,
    OnDestroy,
    OnInit,
    signal,
    WritableSignal
} from '@angular/core';
import { interval, startWith, Subject, Subscription, switchMap } from 'rxjs';
import { OutlineButtonComponent } from '../../../../../button/button.component';
import { IconComponent } from '../../../../../icon/icon.component';
import { PillComponent } from '../../../../../pill/pill.component';
import { UmbracoBlockConfiguration } from '../../../../models/umbraco-blocks.types';
import { TintDirective } from '../../../tint/tint.directive';

export type HeroCarouselConfiguration = {
    image: HeroCarouselConfiguration.Image;
    title: string;
    content: string;
    path?: string;
};

export namespace HeroCarouselConfiguration {
    export type Image = Pick<UmbracoBlockConfiguration.Image, 'path' | 'alt' | 'tint'>;
}

@Component({
    selector: 'nw-carousel-block',
    imports: [
        TintDirective,
        OutlineButtonComponent,
        IconComponent,
        PillComponent,
        NgOptimizedImage
    ],
    templateUrl: './carousel-block.component.html',
    styleUrl: './carousel-block.component.scss'
})
export class CarouselBlockComponent implements OnInit, OnDestroy {
    @Input() config!: HeroCarouselConfiguration[];

    public selectedIndex$$: WritableSignal<number> = signal(0);
    public intervalTime: number = 15000;
    private resetSubject = new Subject<void>();
    private subscription!: Subscription;

    private document: Document = inject(DOCUMENT);

    public ngOnInit(): void {
        this.preloadImages(
            this.config.map(({ image }): HeroCarouselConfiguration.Image => image)
        );
        this.changePage(this.selectedIndex$$());

        this.subscription = this.resetSubject
            .pipe(
                startWith(null),
                switchMap(() => interval(this.intervalTime))
            )
            .subscribe(() => this.changePage(this.selectedIndex$$() + 1));
    }

    public ngOnDestroy(): void {
        if (this.subscription) this.subscription.unsubscribe();
    }

    public changePage(index: number, $event?: Event): void {
        $event?.preventDefault();
        $event?.stopImmediatePropagation();

        if (index < 0) index = this.config.length - 1;
        if (index > this.config.length - 1) index = 0;

        this.resetSubject.next();
        this.selectedIndex$$.set(index);
    }

    public navigateTo(path?: string): void {
        if (!path) return;

        location.assign(path!);
    }

    public preloadImages(images: HeroCarouselConfiguration.Image[]): void {
        images.forEach(({ path }: HeroCarouselConfiguration.Image) => {
            const img = new Image();
            img.src = path;
        });
    }
}
